import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import moment from 'moment';
import { getStudioInDevOverview, updateStudioInDevOverview } from '@sg/services/globalpedia';
import { getDataTime } from '@sg/services/common';
import { DATA_TIME_MODULES, GLOBAL_MIN_DATE } from '@sg/_config/dataTimeModules';
import { generateMonthColumns } from '@sg/pages/StudioManagement/PortfolioOverview/utils';
import type { IPipelineProject } from '@sg/pages/StudioManagement/PortfolioOverview/components/PipelineCalendar/type';
import { getFormatNotes } from '@sg/pages/StudioManagement/InDevProjects/pages/InDevProjectsAll/utils';

export const useInDevProjectsAll = (studioList: any[] = []) => {
  // GameSelect loads unified_id; datatime API expects numeric project_ids for ALL view.
  const projectIds = useMemo(
    () => (studioList || [])
      .map((project) => Number(project?.unified_id ?? project?.project_id))
      .filter((id) => Number.isFinite(id) && id > 0),
    [studioList],
  );
  // Stable effect key so async init does not re-run on array identity churn.
  const projectIdsKey = projectIds.join(',');

  const [selectedMonth, setSelectedMonth] = useState<any>(null);
  const [defaultMonthLoading, setDefaultMonthLoading] = useState<boolean>(true);
  const [maxDataTime, setMaxDataTime] = useState<string | null>(null);
  // 级联筛选器当前选中值
  const [cascadeValue, setCascadeValue] = useState<string[]>([]);
  const [statusValue, setStatusValue] = useState<string[]>([]);
  const [milestoneValue, setMilestoneValue] = useState<string[]>([]);

  const [projects, setProjects] = useState<IPipelineProject[]>([]);
  const [projectListAll, setProjectListAll] = useState<IPipelineProject[]>([]);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notesContent, setNotesContent] = useState<string>('');
  const [studioProjectList, setStudioProjectList] = useState<any[]>([]);
  const [projectOrderList, setProjectOrderList] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const initialNotesRef = useRef<any[]>([]);
  const initialProjectOrderRef = useRef<any[]>([]);

  const monthColumns = useMemo(() => generateMonthColumns(selectedMonth), [selectedMonth]);

  const turnDate = (date: moment.MomentInput) => {
    return date ? moment(date).format('YYYY-MM') : moment().startOf('month').format('YYYY-MM');
  };

  const fetchData = async (selectedMonthValue: moment.MomentInput) => {
    setLoading(true);
    const selectedMonthStr = turnDate(selectedMonthValue);

    try {
      const response: any = await getStudioInDevOverview({
        month: selectedMonthStr,
      });
      const responseData = response?.data?.data || response?.data || response || {};
      const projectList = (responseData?.list || []).map((project: any) => ({
        ...project,
        game_title: project?.game_title || project?.game_name || '', // we format to game_title here instead of direct using game_name from api because most our code alrd use game_title
      }));
      const allProject = (responseData?.studios || []).map((studio: any) => ({
        studio_id: studio?.studio_id,
        studio_name: studio?.studio_name || '',
        projects: (studio?.projects || []).map((project: any) => ({
          project_id: String(project?.project_id || ''),
          game_title: project?.game_title || project?.game_name || '',
        })),
      }));

      setProjectListAll(projectList);

      setProjectOrderList(
        projectList.map((project: any, index: number) => ({
          project_id: String(project?.project_id || ''),
          order: Number(project?.order) || index + 1,
        })),
      );
      initialProjectOrderRef.current = projectList.map((project: any, index: number) => ({
        project_id: String(project?.project_id || ''),
        order: Number(project?.order) || index + 1,
      }));

      setStudioProjectList(allProject);

      const formattedNotes = getFormatNotes(projectList, selectedMonthStr);
      setNotes(formattedNotes);
      initialNotesRef.current = formattedNotes;

      setIsError(false);

      changeFilterProjects(cascadeValue, statusValue, milestoneValue, projectList, allProject, selectedMonthValue);
    } catch (error) {
      setProjectListAll([]);
      setProjects([]);
      // setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);
      const newParams: any = {};
      const hasOrderChanges = JSON.stringify(projectOrderList || [])
        !== JSON.stringify(initialProjectOrderRef.current || []);
      if (projectOrderList?.length > 0 && hasOrderChanges) {
        // 排序提交格式: { orders: [{ game_title, order }, ...] }
        newParams.orders = projectOrderList;
      }
      const baselineMap = new Map<string, { note: string; attach: any[] }>();
      const normalizeAttachForCompare = (attach: any) => {
        let parsed = attach;
        if (typeof parsed === 'string' && parsed.trim()) {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            parsed = [];
          }
        }
        if (!Array.isArray(parsed)) {
          return [];
        }
        return parsed
          .map((f: any) => ({
            name: f?.name || f?.file || '',
            path: f?.path || f?.url || '',
          }))
          .filter((f: any) => f?.name || f?.path)
          .sort((a: any, b: any) => `${a?.name}-${a?.path}`.localeCompare(`${b?.name}-${b?.path}`));
      };

      (initialNotesRef.current || []).forEach((n: any) => {
        const id = String(n?.project_id || '');
        if (!id) {
          return;
        }
        baselineMap.set(id, {
          note: n?.note || '',
          attach: normalizeAttachForCompare(n?.attach),
        });
      });

      const formattedNotes = (notes || [])
        .map((n: any) => {
          const projectId = Number(n?.project_id);
          if (!Number.isFinite(projectId)) {
            return null;
          }
          const currentAttach = normalizeAttachForCompare(n?.attach);
          const baseline = baselineMap.get(String(projectId));
          const changed = !baseline
            || baseline.note !== (n?.note || '')
            || JSON.stringify(baseline.attach) !== JSON.stringify(currentAttach);
          if (!changed) {
            return null;
          }
          return {
            project_id: projectId,
            month: turnDate(n?.date || selectedMonth),
            note: n?.note || '',
            url: Array.isArray(n?.attach)
              ? n.attach.map((f: any) => ({
                name: f?.name,
                path: f?.path || f?.url || '',
              }))
              : [],
          };
        })
        .filter(Boolean);
      if (formattedNotes?.length > 0) {
        newParams.notes = formattedNotes;
      }

      if (!newParams.orders && !newParams.notes) {
        setLoading(false);
        return;
      }

      await updateStudioInDevOverview(newParams);
      // 更新成功后，重新请求接口，刷新数据
      await fetchData(selectedMonth);
    } catch (error) {
      setLoading(false);
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    // Wait until GameSelect finishes loading before querying datatime range.
    if (!projectIds.length) {
      return;
    }

    let cancelled = false;

    const initSelectedMonth = async () => {
      const fallbackMonth = moment().subtract(1, 'month').startOf('month');
      let nextMonth = fallbackMonth;

      try {
        const response = await getDataTime({
          modules: DATA_TIME_MODULES.projects,
          // ALL view must pass every visible project id, not a single reference project.
          project_ids: projectIds,
        });
        if (!cancelled && response?.code === 0 && response?.data?.max_datatime) {
          setMaxDataTime(response.data.max_datatime);
          // Default month follows latest available data month from backend.
          nextMonth = moment(response.data.max_datatime, 'YYYY-MM').startOf('month');
        } else if (!cancelled) {
          setMaxDataTime(null);
        }
      } catch {
        // Fallback keeps previous-month behavior if datatime API is temporarily unavailable.
        if (!cancelled) {
          setMaxDataTime(null);
        }
      }

      if (!cancelled) {
        setSelectedMonth(nextMonth);
        await fetchData(nextMonth);
        setDefaultMonthLoading(false);
      }
    };

    setDefaultMonthLoading(true);
    initSelectedMonth();

    // Ignore stale datatime responses when project list changes during fetch.
    return () => {
      cancelled = true;
    };
  }, [projectIdsKey]);

  const disabledMonth = useCallback((date: moment.Moment) => {
    // MonthPicker disabledMonth returns true for enabled months.
    // Keep range aligned with other Studio Management tabs: GLOBAL_MIN_DATE ~ max_datatime.
    if (!maxDataTime) {
      return false;
    }

    const minDate = moment(GLOBAL_MIN_DATE, 'YYYY-MM');
    const maxDate = moment(maxDataTime, 'YYYY-MM');

    return !date.isBefore(minDate, 'month') && !date.isAfter(maxDate, 'month');
  }, [maxDataTime]);

  const handleDateFilterChange = (selectedMonthValue: moment.MomentInput) => {
    fetchData(selectedMonthValue);
  };

  // 项目拖拽排序：保留完整 project 对象，附 order 字段，未在可视集合中的项目放在末尾
  const handleProjectsReorder = (reorderedProjects: any[]) => {
    const updated = (reorderedProjects || []).map((project, index) => ({
      ...project,
      order: index + 1,
    }));

    // 同步可视项目（用于当前列表渲染）
    setProjects([...updated]);

    // 同步底层分类源（保留过滤场景下未在可视集合中的项目）
    const sourceList = projectListAll;
    const visibleProjectIds = new Set(updated.map((p: any) => String(p?.project_id || '')));
    const others = (sourceList || []).filter((p: any) => !visibleProjectIds.has(String(p?.project_id || '')));
    const merged = [...updated, ...others].map((p: any, i: number) => ({
      ...p,
      order: i + 1,
    }));

    // 维护提交格式: { orders: [{ game_title, order }, ...] }
    setProjectOrderList(
      merged.map((p: any) => ({
        project_id: String(p?.project_id || ''),
        order: p?.order,
      })),
    );
  };

  const handleProjectsReorderCancel = () => {
    setProjectOrderList([...(initialProjectOrderRef.current || [])]);
    changeFilterProjects();
  };

  const changeFilterProjects = (
    cascadeValueTemp = cascadeValue,
    statusValueTemp = statusValue,
    milestoneValueTemp = milestoneValue,
    projectListAllTemp = projectListAll,
    allProjectTemp = studioProjectList,
    selectedMonthValueTemp = selectedMonth,
  ) => {
    let projectFilterList = [...projectListAllTemp];
    // 过滤statusValueTemp
    if (!(statusValueTemp?.includes('all') || statusValueTemp?.length === 0)) {
      const newProjectList = projectFilterList?.filter((project: any) => {
        return statusValueTemp?.includes(project?.status);
      });
      projectFilterList = [...(newProjectList || [])];
    }

    // 过滤milestoneValueTemp
    if (!(milestoneValueTemp?.includes('all') || milestoneValueTemp?.length === 0)) {
      const newProjectList = projectFilterList?.filter((project: any) => {
        return milestoneValueTemp?.includes(project?.milestone_category);
      });
      projectFilterList = [...(newProjectList || [])];
    }
    const projectFilterListAllTemp = [...projectFilterList];
    let projectsTemp = [...projectFilterListAllTemp];
    let projectsNotesTemp = [...projectFilterListAllTemp];

    // 过滤cascadeValueTemp
    if (!(cascadeValueTemp?.includes('all') || cascadeValueTemp?.length === 0)) {
      const idToTitle = new Map<string, string>();
      (allProjectTemp || []).forEach((studio: any) => {
        (studio?.projects || []).forEach((p: any) => {
          if (p?.project_id) {
            idToTitle.set(String(p.project_id), p?.game_title);
          }
        });
      });

      const gameTitles = cascadeValueTemp?.map((v) => idToTitle.get(v))?.filter(Boolean) as string[];
      const newProjectList = projectsTemp?.filter((project: any) => {
        return gameTitles?.includes(project?.game_title);
      });
      projectsTemp = [...(newProjectList || [])];
      const newProjectNotesList = [...projectsNotesTemp]?.filter((project: any) => {
        return gameTitles?.includes(project?.game_title);
      });
      projectsNotesTemp = [...(newProjectNotesList || [])];
    }

    setNotes(getFormatNotes(projectsNotesTemp, turnDate(selectedMonthValueTemp)));

    setProjects([...projectsTemp]);
  };

  return {
    projects,
    projectListAll,
    monthColumns,
    loading: loading || defaultMonthLoading,
    notesContent,
    handleProjectsReorder,
    handleProjectsReorderCancel,
    handleDateFilterChange,
    disabledMonth,
    studioProjectList,
    handleConfirm,
    setNotesContent,

    isError,
    notes,
    setNotes,
    setProjects,
    setProjectOrderList,
    projectOrderList,
    selectedMonth,
    setSelectedMonth,
    cascadeValue,
    setCascadeValue,
    changeFilterProjects,
    statusValue,
    setStatusValue,
    milestoneValue,
    setMilestoneValue,
  };
};
